import i18n from '@shared/utils/i18n';

interface ErrorWithStatus {
  status?: number | string;
  data?: { message?: string };
  message?: string;
}

interface ErrorWithData {
  data?: { message?: string };
  message?: string;
  status?: number | string;
}

export const extractApiErrorMessage = (
  error: unknown,
  defaultMessage: string = i18n.t('agentPage.knowledgeBase.errors.generic'),
): string => {
  if (!error || typeof error !== 'object') {
    return defaultMessage;
  }

  const err = error as ErrorWithData;

  if (err.data?.message && typeof err.data.message === 'string') {
    return err.data.message;
  }

  if (err.message && typeof err.message === 'string') {
    return err.message;
  }

  return defaultMessage;
};

export const getUserFriendlyErrorMessage = (
  error: unknown,
  context: 'upload' | 'delete' | 'fetch' = 'fetch',
): string => {
  if (!error || typeof error !== 'object') {
    return getDefaultErrorMessage(context);
  }

  const err = error as ErrorWithStatus;

  const extractedMessage = extractApiErrorMessage(error, '');
  if (extractedMessage && extractedMessage !== i18n.t('agentPage.knowledgeBase.errors.generic')) {
    return extractedMessage;
  }

  if ('status' in err && err.status !== undefined) {
    const status = err.status;

    if (status === 'FETCH_ERROR') {
      return i18n.t('agentPage.knowledgeBase.errors.network');
    }

    if (typeof status === 'number') {
      switch (status) {
        case 400:
          return context === 'upload'
            ? 'Invalid file format. Only PDF files are allowed.'
            : 'Invalid request. Please check your input and try again.';
        case 401:
        case 403:
          return `You do not have permission to ${context} files.`;
        case 404:
          return context === 'delete'
            ? 'File not found. It may have already been deleted.'
            : context === 'fetch'
              ? 'Campaign not found.'
              : 'Resource not found.';
        case 413:
          return 'File is too large. Maximum file size is 50MB.';
        case 500:
        case 502:
        case 503:
        case 504:
          return 'Server error. Please try again later.';
        default:
          if (status >= 500) {
            return 'Server error. Please try again later.';
          }
          if (status >= 400) {
            return 'Request error. Please check your input and try again.';
          }
      }
    }
  }

  return getDefaultErrorMessage(context);
};

const getDefaultErrorMessage = (context: 'upload' | 'delete' | 'fetch'): string => {
  switch (context) {
    case 'upload':
      return i18n.t('agentPage.knowledgeBase.errors.uploadFailed');
    case 'delete':
      return i18n.t('agentPage.knowledgeBase.errors.deleteFailed');
    case 'fetch':
      return i18n.t('agentPage.knowledgeBase.errors.fetchFailed');
    default:
      return i18n.t('agentPage.knowledgeBase.errors.generic');
  }
};
